import {
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    NgZone,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { utils, writeFile } from 'xlsx';

import { CmpsDatePipe } from '../common/date.pipe';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import { CellNode, Column, GridComponent } from './experimental.grid';
import { ColumnID, DataSource } from './table-manager-report.tdcf.model';
import { TranslationKeyMap } from './tdcf-form.i18n';
import { ManagerReportTableState, storeStateMapper } from './tdcf-form.state';

@Component({
    selector: '[cmpsTdcfReportManagerTable]',
    templateUrl: './table-manager-report.tdcf.component.html',
    host: {
        class: 'cmps-tdcf-report-manager-table',
    },
})
export class TableManagerReportTdcfComponent
    implements StateToProps<ManagerReportTableState>
{
    @ViewChild('grid', { static: true }) private readonly _grid: GridComponent;
    @ViewChild('thTpl', { static: true })
    private readonly _thTpl: TemplateRef<any>;

    @ViewChild('tdTpl', { static: true })
    private readonly _tdTpl: TemplateRef<any>;

    @Input() directTeamOnly: boolean;

    i18n = TranslationKeyMap;
    ColumnID = ColumnID;
    private _storeSub: Subscription;
    state: ManagerReportTableState;
    dataSource: DataSource;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _translateService: TranslateService,
        @Inject(STORE) private readonly _store: Store,
        private readonly _datePipe: CmpsDatePipe,
    ) {}

    ngOnInit(): void {
        this._storeSub = storeObserver<ManagerReportTableState>(
            this._store,
            (state) => storeStateMapper(state).managerReport.table,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        this.state = state;
                        this.stateToProps();
                    }),
                ),
            )
            .subscribe();

        this.dataSource = new DataSource();
    }

    ngAfterViewInit(): void {
        this.dataSource.thTpl = this._thTpl;
        this.dataSource.tdTpl = this._tdTpl;
        this.dataSource.state = this.state;
        this.dataSource.directTeamOnly = this.directTeamOnly;
        this.dataSource.setData();

        this._changeDetectorRef.detectChanges();

        this._grid.grid.setChildren(this.dataSource.headerRows);
        this._grid.grid.appendChildren(this.dataSource.makeRows());
    }

    ngOnDestroy(): void {
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(): void {
        if (this.dataSource) {
            this.dataSource.state = this.state;

            this._grid.grid.setChildren(this.dataSource.headerRows);
            this._grid.grid.appendChildren(this.dataSource.makeRows());
        }
    }

    columnName(column: Column): string {
        switch (column.id) {
            case ColumnID.QUARTER.toString():
                return TranslationKeyMap._201_QUARTER;
            case ColumnID.USER_ID.toString():
                return TranslationKeyMap._101_USER_ID;
            case ColumnID.FIRST_NAME.toString():
                return TranslationKeyMap._42_FIRST_NAME;
            case ColumnID.LAST_NAME.toString():
                return TranslationKeyMap._46_LAST_NAME;
            case ColumnID.STATUS.toString():
                return TranslationKeyMap._127_STATUS;
            case ColumnID.MANAGER_USER_ID.toString():
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `${this._translateService.instant(
                    TranslationKeyMap._48_MANAGER,
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                )} ${this._translateService.instant(
                    TranslationKeyMap._101_USER_ID,
                )}`;
            case ColumnID.MANAGER_FIRST_NAME.toString():
                return TranslationKeyMap._7214_MANAGER_FIRST_NAME;
            case ColumnID.MANAGER_LAST_NAME.toString():
                return TranslationKeyMap._7215_MANAGER_LAST_NAME;
        }
        return null;
    }

    cellValue(column: Column, cell: CellNode): any {
        switch (column.id) {
            case ColumnID.QUARTER.toString():
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return !cell.def.ctx.start
                    ? TranslationKeyMap._842_N_A
                    : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      `${cell.def.ctx.displayName} (${this._datePipe.transform(
                          cell.def.ctx.start,
                          'MMMM dd, y',
                          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      )} - ${this._datePipe.transform(
                          cell.def.ctx.end,
                          'MMMM dd, y',
                      )})`;
            case ColumnID.USER_ID.toString():
                return cell.def.ctx.userId === null
                    ? ''
                    : cell.def.ctx.userId.toString();
            case ColumnID.FIRST_NAME.toString():
                return cell.def.ctx.firstName;
            case ColumnID.LAST_NAME.toString():
                return cell.def.ctx.lastName;
            case ColumnID.STATUS.toString():
                // TODO change to flowstate
                if (cell.def.ctx.status === 'Pending') {
                    return TranslationKeyMap._7222_PENDING;
                }
                if (cell.def.ctx.status === 'Completed') {
                    return TranslationKeyMap._7223_COMPLETED;
                }
                if (cell.def.ctx.status === 'Not Started') {
                    return TranslationKeyMap._7220_NOT_STARTED;
                }
                return cell.def.ctx.status;
            case ColumnID.MANAGER_USER_ID.toString():
                return cell.def.ctx.managerUserId === null
                    ? ''
                    : cell.def.ctx.managerUserId.toString();
            case ColumnID.MANAGER_FIRST_NAME.toString():
                return cell.def.ctx.managerFirstName;
            case ColumnID.MANAGER_LAST_NAME.toString():
                return cell.def.ctx.managerLastName;
        }
        return null;
    }

    onExportDataClick(): void {
        const data: any[][] = [];

        this.dataSource.headerRows.forEach((r) => {
            data.push(
                r.cells.map((l) => {
                    // eslint-disable-next-line eqeqeq
                    const column = this.dataSource.columns.find(
                        // eslint-disable-next-line eqeqeq
                        (c) => c.id == l.columnId,
                    );
                    const value = this.columnName(column);
                    return !value
                        ? null
                        : this._translateService.instant(value);
                }),
            );
        });

        this._grid.grid.children
            .filter((r, i) => i !== 0 /* skip header */)
            .forEach((r) => {
                data.push(
                    r.cells.children.map((l) => {
                        // eslint-disable-next-line eqeqeq
                        const column = this.dataSource.columns.find(
                            // eslint-disable-next-line eqeqeq
                            (c) => c.id == l.def.columnId,
                        );
                        const value = this.cellValue(column, l);
                        return !value
                            ? null
                            : this._translateService.instant(value);
                    }),
                );
            });

        const ws = utils.aoa_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Sheet1');
        writeFile(
            wb,
            this.directTeamOnly
                ? 'QTR Check In Form My Team Report.xlsx'
                : 'QTR Check In Form All Report.xlsx',
        );
    }
}
